import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  Box,
} from "@mui/material";
import { MdVisibility, MdSave } from "react-icons/md";

import { Surface, SectionTitle, ConfirmDialog } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

const FIELDS = [
  { key: "accountHolderName", label: "Account holder name", required: true },
  { key: "accountNumber", label: "Account number", required: true },
  { key: "ifscCode", label: "IFSC code", required: true },
  { key: "bankName", label: "Bank name" },
  { key: "branchName", label: "Branch" },
];

/**
 * Where this partner's money goes.
 *
 * Two things here are deliberate and both are about the same risk — a wrong
 * or swapped account number is the failure that cannot be undone:
 *
 *   • The account number is masked until someone asks for it, and asking is
 *     itself audited with a reason. This is the one read in the console that
 *     would matter in a fraud investigation.
 *   • Saving requires a reason. Changing where money goes is the single most
 *     abusable action available here.
 */
export default function BankTab({ partnerId, notify }) {
  const [bank, setBank] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(
    async (opts) => {
      setLoading(true);
      setError("");
      try {
        const data = await api.getBank(partnerId, opts);
        setBank(data.bank);
        setValues(data.bank);
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load bank details.");
      } finally {
        setLoading(false);
      }
    },
    [partnerId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const dirty = FIELDS.some(
    (f) => String(values?.[f.key] ?? "") !== String(bank?.[f.key] ?? "")
  );

  return (
    <Stack spacing={2}>
      <Surface sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.2 }}
        >
          <SectionTitle sx={{ mb: 0 }}>Bank account</SectionTitle>
          {!bank?.revealed && (
            <Button
              size="small"
              startIcon={<MdVisibility />}
              onClick={() => setDialog({ kind: "reveal" })}
              sx={{ textTransform: "none", color: T.secondary }}
            >
              Reveal account number
            </Button>
          )}
        </Stack>

        {bank?.revealed && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: "0.83rem" }}>
            The full account number is shown. This view was recorded in the
            partner's activity log.
          </Alert>
        )}

        <Grid container spacing={2}>
          {FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.key}>
              <TextField
                fullWidth
                size="small"
                label={field.label}
                required={field.required}
                value={values?.[field.key] ?? ""}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [field.key]: e.target.value }))
                }
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  ...(field.key === "accountNumber" && {
                    "& input": { fontFamily: "monospace" },
                  }),
                }}
              />
            </Grid>
          ))}
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2.5 }}>
          <Box sx={{ mr: "auto" }}>
            <Typography sx={{ fontSize: "0.8rem", color: T.subtle }}>
              {dirty ? "Unsaved changes" : "No changes"}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<MdSave />}
            disabled={!dirty || loading}
            onClick={() => setDialog({ kind: "save" })}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: T.primary,
              "&:hover": { bgcolor: "#013838" },
            }}
          >
            Save bank details
          </Button>
        </Stack>
      </Surface>

      <Surface
        sx={{
          p: 2,
          borderColor: tint(T.warning, 0.35),
          bgcolor: tint(T.warning, 0.05),
        }}
      >
        <Typography sx={{ fontSize: "0.83rem", color: T.ink }}>
          A payout into a valid stranger's account cannot be recalled. Check the
          account number against a document the partner supplied, not against
          what they read out over the phone.
        </Typography>
      </Surface>

      <ConfirmDialog
        open={dialog?.kind === "reveal"}
        title="Reveal the full account number?"
        description="This is recorded in the partner's activity log, with your name against it."
        confirmLabel="Reveal"
        requireReason
        reasonLabel="Why do you need to see it?"
        onConfirm={(reason) => load({ reveal: true, reason })}
        onClose={() => setDialog(null)}
      />

      <ConfirmDialog
        open={dialog?.kind === "save"}
        title="Change this partner's bank details?"
        description="Future payouts will go to this account. The change is recorded in the activity log."
        confirmLabel="Save"
        destructive
        requireReason
        onConfirm={async (reason) => {
          await api.updateBank(partnerId, { ...values, reason });
          notify?.("Bank details updated.");
          await load();
        }}
        onClose={() => setDialog(null)}
      />
    </Stack>
  );
}
